import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getStoredUser, clearSession, setSession as persist } from "./api";

const AuthCtx = createContext(null);

const ALL_PERMS = [
  "earn", "redeem", "view_customers", "create_customer",
  "manage_offers", "view_ledger", "view_reports", "approve_rewards",
];

/** Merge the auth-response `user` with the fresh `/auth/me` profile so we
 *  always have `permissions` (staff/admin) and the latest name/email. */
function mergeProfile(baseUser, data) {
  if (!baseUser) return null;
  return { ...baseUser, profile: data.profile, role: data.role };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser((prev) => (prev ? mergeProfile(prev, data) : prev));
      return data;
    } catch {
      clearSession();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    // If returning from Google OAuth, skip /me check — GoogleAuthCallback will exchange the session_id and set the JWT.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      await refreshMe();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Staff/admin permissions can change while a session is open (admin toggles
  // them). Refresh on tab-focus so a staff user sees the updated capabilities
  // without needing to log out.
  useEffect(() => {
    if (!user || user.role === "customer") return;
    const onFocus = () => { refreshMe(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") refreshMe();
    });
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refreshMe]);

  const login = (token, u) => {
    persist(token, u);
    setUser(u);
    // Kick a background refresh so permissions land ASAP after login.
    setTimeout(() => { refreshMe(); }, 0);
  };
  const logout = () => {
    clearSession();
    setUser(null);
  };

  // Derived: permission map + hasPerm helper.
  // - Super admins bypass all checks (returns true for every permission).
  // - Staff and regular admins (managers) read from `profile.permissions`;
  //   missing entries default to true (mirrors the backend behaviour).
  const isSuperAdmin = !!user?.profile?.super_admin;
  const permissions = (() => {
    if (!user || user.role === "customer") return {};
    if (isSuperAdmin) return ALL_PERMS.reduce((a, k) => ({ ...a, [k]: true }), {});
    const p = user.profile?.permissions || {};
    return ALL_PERMS.reduce((a, k) => ({ ...a, [k]: p[k] !== false }), {});
  })();
  const hasPerm = (name) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    return permissions[name] !== false;
  };

  return (
    <AuthCtx.Provider
      value={{ user, setUser, login, logout, loading, refreshMe, permissions, hasPerm, isSuperAdmin }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
